import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useExamLock } from '@/hooks/useExamLock';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { validateCandidateToken } from '@/lib/candidateToken';
import { TestEngine } from '@/components/test/TestEngine';
import { ML8Runner } from '@/components/test/ML8Runner';
import { APO6Runner } from '@/components/test/APO6Runner';
import { RA10Runner } from '@/components/test/RA10Runner';
import { RFBSJRunner } from '@/components/test/RFBSJRunner';
import { CultureFitRunner } from '@/components/test/CultureFitRunner';
import {
  TrueFalseNoSeRunner,
  type TrueFalseNoSeItem,
  type TrueFalseNoSeAnswer,
} from '@/components/test/JKOfficeAdminRunner';
import { PCL_10_ITEMS, EEL_12_ITEMS, MINI_IPIP_20_ITEMS } from '@/config/instruments';
import { getCommercialName } from '@/config/batteries';
import { getInstrumentsToRun, type BatteryModule } from '@/lib/candidateBattery';
import { computeResults } from '@/lib/computeResults';
import { updateMiniIpipNorms, type MiniIpipDimKey } from '@/lib/miniIpipNorms';
import { updateScalarNorm } from '@/lib/scalarNorms';
import { pickRandomJKOfficeAdminItems } from '@/config/jkOfficeAdminItems';
import { pickRandomJKSalesRetailItems } from '@/config/jkSalesRetailItems';
const JK_ITEMS_COUNT = 10;

export function TestPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [validation, setValidation] = useState<Awaited<ReturnType<typeof validateCandidateToken>> | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [modules, setModules] = useState<BatteryModule[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [jkItems, setJkItems] = useState<Record<string, TrueFalseNoSeItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { locked: examLocked } = useExamLock(attemptId ?? token ?? null);
  const saveDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!token) return;
    validateCandidateToken(token)
      .then((v) => {
        if (!v.valid) {
          navigate(v.reason === 'completed' ? '/c/error/completed' : '/c/error/invalid', { replace: true });
          return;
        }
        setValidation(v);
      })
      .catch((err) => {
        if (import.meta.env?.DEV) console.warn('[TestPage] Error validando token:', err);
        navigate('/c/error/invalid', { replace: true, state: { error: String(err?.message ?? err) } });
      });
  }, [token, navigate]);

  useEffect(() => {
    if (!validation?.candidateId || !token || !validation.processId) {
      if (validation && !validation.candidateId) setLoadError('Datos de invitación incompletos.');
      setLoading(false);
      return;
    }
    setLoadError(null);
    const loadOrCreateAttempt = async () => {
      try {
        const processIdVal = validation!.processId!;

        const q = query(
          collection(db, 'attempts'),
          where('candidateId', '==', validation!.candidateId),
          where('processId', '==', processIdVal)
        );
        const snap = await getDocs(q);

        let attId: string;
        if (!snap.empty) {
          const first = snap.docs[0];
          const attData = first.data();
          if (attData.status === 'completed') {
            navigate('/c/error/completed', { replace: true });
            return;
          }
          attId = first.id;
          setAttemptId(attId);

          const respSnap = await getDoc(doc(db, 'responses', attId));
          if (respSnap.exists()) {
            const data = respSnap.data();
            setAnswers((data?.answers as Record<string, unknown>) ?? {});
          }
        } else {
          let excludeFromNorms = false;
          if (validation!.inviteId) {
            const invSnap = await getDoc(doc(db, 'invites', validation!.inviteId));
            const invData = invSnap.exists() ? invSnap.data() : {};
            excludeFromNorms = invData?.excludeFromNorms === true;
          }
          const ref = await addDoc(collection(db, 'attempts'), {
            companyId: validation!.companyId,
            processId: processIdVal,
            candidateId: validation!.candidateId,
            inviteId: validation!.inviteId,
            excludeFromNorms,
            startedAt: new Date().toISOString(),
            progress: 0,
            status: 'in_progress',
          });
          attId = ref.id;
          setAttemptId(attId);
        }

        const [processSnap, batterySnap] = await Promise.all([
          getDoc(doc(db, 'processes', processIdVal)),
          getDoc(doc(db, 'processBatteries', processIdVal)),
        ]);

        const rawTestKeys = processSnap.exists() ? processSnap.data()?.testKeys : null;
        const rawModuleIds = batterySnap.exists() ? batterySnap.data()?.moduleIds : null;
        const processTestKeys = Array.isArray(rawTestKeys) ? rawTestKeys : [];
        const batteryModuleIds = Array.isArray(rawModuleIds) ? rawModuleIds : [];

        const instruments = getInstrumentsToRun(processTestKeys, batteryModuleIds);

        if (instruments.length === 0) {
          setModules([{ instrumentId: 'MINI_IPIP_20', testKey: 'perfil_personalidad' }]);
        } else {
          setModules(instruments);
        }
      } catch (err) {
        if (import.meta.env?.DEV) console.error('[TestPage] Error cargando evaluación:', err);
        const msg = err instanceof Error ? err.message : String(err);
        setLoadError(msg.includes('index') ? 'Configuración pendiente. Contacta al administrador.' : msg);
      } finally {
        setLoading(false);
      }
    };
    loadOrCreateAttempt();
  }, [validation, token, navigate]);

  const saveResponses = useCallback(
    async (newAnswers: Record<string, unknown>) => {
      if (!attemptId) return;
      const merged = { ...answers, ...newAnswers };
      setAnswers(merged);
      await setDoc(
        doc(db, 'responses', attemptId),
        { answers: merged, updatedAt: new Date().toISOString() },
        { merge: true }
      );
    },
    [attemptId, answers]
  );

  const AUTO_SAVE_DEBOUNCE_MS = 1500;
  const createTestEngineAnswerHandler = useCallback(
    (instrumentId: string) =>
      (_itemId: string, _value: number, allAnswers: Record<string, number>) => {
        const key = instrumentId;
        if (saveDebounceRef.current[key]) clearTimeout(saveDebounceRef.current[key]);
        saveDebounceRef.current[key] = setTimeout(() => {
          saveResponses({ [instrumentId]: allAnswers });
          delete saveDebounceRef.current[key];
        }, AUTO_SAVE_DEBOUNCE_MS);
      },
    [saveResponses]
  );

  useEffect(() => {
    return () => {
      Object.values(saveDebounceRef.current).forEach(clearTimeout);
      saveDebounceRef.current = {};
    };
  }, []);

  const getOrPickJkItems = useCallback(
    (instrumentId: string): TrueFalseNoSeItem[] => {
      const saved = answers[instrumentId] as { itemsPresented?: TrueFalseNoSeItem[] } | undefined;
      if (saved?.itemsPresented && saved.itemsPresented.length >= JK_ITEMS_COUNT) {
        return saved.itemsPresented;
      }
      if (jkItems[instrumentId]) return jkItems[instrumentId];

      const pick =
        instrumentId === 'JK_OFFICE_ADMIN'
          ? pickRandomJKOfficeAdminItems(JK_ITEMS_COUNT)
          : pickRandomJKSalesRetailItems(JK_ITEMS_COUNT);

      const items: TrueFalseNoSeItem[] = pick.map((i) => ({
        id: i.id,
        correctAnswer: i.correctAnswer,
        text: i.text,
      }));
      setJkItems((prev) => ({ ...prev, [instrumentId]: items }));

      saveResponses({
        [instrumentId]: { itemsPresented: items, answers: (saved as { answers?: Record<string, TrueFalseNoSeAnswer> })?.answers ?? {} },
      });

      return items;
    },
    [answers, jkItems, saveResponses]
  );

  const handleModuleComplete = useCallback(
    async (instrumentId: string, data: unknown) => {
      if (!attemptId || !validation?.candidateId || !token) return;

      const newAnswers: Record<string, unknown> = {};
      if (instrumentId === 'RFB_SJ_8' && data && typeof data === 'object' && 'firstChoices' in data && 'avoidChoices' in data) {
        newAnswers[instrumentId] = data;
      } else if (instrumentId === 'JK_OFFICE_ADMIN' || instrumentId === 'JK_SALES_RETAIL') {
        const jkData = data as { answers: Record<string, TrueFalseNoSeAnswer>; itemsPresented: { id: string; correctAnswer: 'V' | 'F' }[] };
        newAnswers[instrumentId] = jkData;
      } else {
        newAnswers[instrumentId] = data;
      }

      await saveResponses(newAnswers);

      if (currentIndex >= modules.length - 1) {
        const allAnswers = { ...answers, ...newAnswers };

        const [processSnap, companySnap, attemptSnap] = await Promise.all([
          getDoc(doc(db, 'processes', validation.processId!)),
          getDoc(doc(db, 'companies', validation.companyId)),
          getDoc(doc(db, 'attempts', attemptId)),
        ]);
        const processData = processSnap.exists() ? processSnap.data() : {};
        const companyData = companySnap.exists() ? companySnap.data() : {};
        const attemptData = attemptSnap.exists() ? attemptSnap.data() : {};
        const excludeFromNorms =
          attemptData.excludeFromNorms === true || processData.excludeFromNorms === true;
        const industryKey = (companyData.industry as string) ?? null;
        const countryCode = (companyData.country as string) ?? null;

        const normContext = {
          companyId: validation.companyId,
          industryKey: industryKey || null,
          countryCode: countryCode || null,
        };

        const { scores, moduleAggregates } = await computeResults({
          answers: allAnswers,
          normContext,
        });

        const stripUndefined = (obj: unknown): unknown => {
          if (obj === undefined) return null;
          if (Array.isArray(obj)) return obj.map(stripUndefined);
          if (obj != null && typeof obj === 'object') {
            const out: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(obj)) {
              if (v !== undefined) out[k] = stripUndefined(v);
            }
            return out;
          }
          return obj;
        };

        const payload = stripUndefined({
          companyId: validation.companyId,
          scores,
          moduleAggregates,
          version: 1,
          computedAt: new Date().toISOString(),
        }) as Record<string, unknown>;

        await setDoc(doc(db, 'results', attemptId), payload);
        await updateDoc(doc(db, 'attempts', attemptId), {
          progress: 100,
          status: 'completed',
          completedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        await updateDoc(doc(db, 'candidates', validation.candidateId), { status: 'completed' });

        const normParams = {
          companyId: validation.companyId,
          industryKey,
          countryCode,
          excludeFromNorms,
        };

        const runNormUpdates = async () => {
          const miniAgg = moduleAggregates.MINI_IPIP_20 as { dimensions?: Record<string, { avg: number }> } | undefined;
          if (miniAgg?.dimensions) {
            const dimAvgs = {} as Record<MiniIpipDimKey, number>;
            for (const k of ['responsabilidadOrden', 'estabilidadEmocional', 'energiaSocial', 'cooperacion', 'aperturaAdaptabilidad'] as const) {
              const d = miniAgg.dimensions[k];
              if (d && typeof d.avg === 'number') dimAvgs[k] = d.avg;
            }
            if (Object.keys(dimAvgs).length === 5) {
              await updateMiniIpipNorms({ ...normParams, dimAvgs });
            }
          }
          const eel = moduleAggregates.EEL_12 as { score50?: number } | undefined;
          const ra = moduleAggregates.RA_10 as { score50?: number } | undefined;
          const pcl = moduleAggregates.PCL_10 as { totalScore?: number } | undefined;
          const promises: Promise<void>[] = [];
          if (eel?.score50 != null) {
            promises.push(updateScalarNorm({ ...normParams, testId: 'EEL_12', scorePercent: Math.round((eel.score50 / 50) * 100) }));
          }
          if (ra?.score50 != null) {
            promises.push(updateScalarNorm({ ...normParams, testId: 'RA_10', scorePercent: Math.round((ra.score50 / 50) * 100) }));
          }
          if (pcl?.totalScore != null) {
            promises.push(updateScalarNorm({ ...normParams, testId: 'PCL_10', scorePercent: Math.round((pcl.totalScore / 50) * 100) }));
          }
          await Promise.allSettled(promises);
        };
        runNormUpdates().catch((e) => {
          if (import.meta.env?.DEV) console.warn('[TestPage] Error actualizando normas:', e);
        });

        navigate(`/c/${token}/finish`);
      } else {
        setCurrentIndex((i) => i + 1);
        const progress = Math.round(((currentIndex + 1) / modules.length) * 100);
        await updateDoc(doc(db, 'attempts', attemptId), {
          progress,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [
      attemptId,
      validation,
      token,
      currentIndex,
      modules.length,
      answers,
      jkItems,
      getOrPickJkItems,
      saveResponses,
      navigate,
    ]
  );

  if (!validation?.valid || loading || loadError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
        {loadError ? (
          <>
            <p className="text-center text-destructive">{loadError}</p>
            <button
              type="button"
              className="text-sm text-primary underline hover:no-underline"
              onClick={() => navigate('/c/error/invalid', { replace: true })}
            >
              Volver al inicio
            </button>
          </>
        ) : (
          <p className="text-muted-foreground">Cargando...</p>
        )}
      </div>
    );
  }

  if (examLocked) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-800 dark:bg-amber-950/30">
          <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
            Examen abierto en otra pestaña
          </h2>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            Este examen ya está en curso en otra ventana o pestaña. Cierra la otra pestaña para continuar aquí, o continúa en la pestaña donde lo iniciaste.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Por seguridad, solo puedes realizar el examen en una pestaña a la vez.
          </p>
        </div>
      </div>
    );
  }

  const module = modules[currentIndex];
  if (!module) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
        <p className="text-muted-foreground">
          No hay pruebas configuradas para este proceso.
        </p>
        <p className="text-sm text-muted-foreground">
          El reclutador debe configurar la batería en el proceso antes de que puedas realizar la evaluación.
        </p>
        <button
          type="button"
          className="text-sm text-primary underline hover:no-underline"
          onClick={() => navigate('/c/error/invalid', { replace: true })}
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  const { instrumentId, testKey } = module;
  const commercialName = getCommercialName(testKey);

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="mb-4 text-center text-sm text-muted-foreground">
        Prueba {currentIndex + 1} de {modules.length}
      </div>

      {instrumentId === 'PCL_10' && (
        <TestEngine
          testId="PCL_10"
          commercialName={commercialName}
          items={PCL_10_ITEMS}
          onComplete={(a) => handleModuleComplete('PCL_10', a)}
          initialAnswers={(answers.PCL_10 as Record<string, number>) ?? {}}
          onAnswerChange={createTestEngineAnswerHandler('PCL_10')}
          persistKey={attemptId ? `exam_${attemptId}_PCL_10` : undefined}
        />
      )}

      {instrumentId === 'EEL_12' && (
        <TestEngine
          testId="EEL_12"
          commercialName={commercialName}
          items={EEL_12_ITEMS}
          onComplete={(a) => handleModuleComplete('EEL_12', a)}
          initialAnswers={(answers.EEL_12 as Record<string, number>) ?? {}}
          onAnswerChange={createTestEngineAnswerHandler('EEL_12')}
          persistKey={attemptId ? `exam_${attemptId}_EEL_12` : undefined}
        />
      )}

      {instrumentId === 'MINI_IPIP_20' && (
        <TestEngine
          testId="MINI_IPIP_20"
          commercialName={commercialName}
          items={MINI_IPIP_20_ITEMS}
          instruction="Instrucciones: Lee cada afirmación y marca qué tan de acuerdo estás. Responde con honestidad; no hay respuestas correctas o incorrectas."
          onComplete={(a) => handleModuleComplete('MINI_IPIP_20', a)}
          initialAnswers={(answers.MINI_IPIP_20 as Record<string, number>) ?? {}}
          onAnswerChange={createTestEngineAnswerHandler('MINI_IPIP_20')}
          persistKey={attemptId ? `exam_${attemptId}_MINI_IPIP_20` : undefined}
        />
      )}

      {instrumentId === 'RA_10' && (
        <RA10Runner
          commercialName={commercialName}
          onComplete={(c) => handleModuleComplete('RA_10', c)}
          initialChoices={(answers.RA_10 as import('@/config/instruments').RA10OptionLetter[]) ?? []}
        />
      )}

      {instrumentId === 'RFB_SJ_8' && (
        <RFBSJRunner
          commercialName={commercialName}
          onComplete={(f, a) => handleModuleComplete('RFB_SJ_8', { firstChoices: f, avoidChoices: a })}
          initialFirst={(answers.RFB_SJ_8 as { firstChoices?: import('@/config/instruments').RFBOptionLetter[] })?.firstChoices ?? []}
          initialAvoid={(answers.RFB_SJ_8 as { avoidChoices?: import('@/config/instruments').RFBOptionLetter[] })?.avoidChoices ?? []}
        />
      )}

      {instrumentId === 'JK_OFFICE_ADMIN' &&
        (() => {
          const items = getOrPickJkItems('JK_OFFICE_ADMIN');
          return (
            <TrueFalseNoSeRunner
              commercialName={commercialName}
              items={items}
              onComplete={(ans) =>
                handleModuleComplete('JK_OFFICE_ADMIN', {
                  answers: ans,
                  itemsPresented: items.map((i) => ({ id: i.id, correctAnswer: i.correctAnswer })),
                })
              }
              initialAnswers={
                ((answers.JK_OFFICE_ADMIN as { answers?: Record<string, TrueFalseNoSeAnswer> })?.answers) ?? {}
              }
            />
          );
        })()}

      {instrumentId === 'JK_SALES_RETAIL' &&
        (() => {
          const items = getOrPickJkItems('JK_SALES_RETAIL');
          return (
            <TrueFalseNoSeRunner
              commercialName={commercialName}
              items={items}
              onComplete={(ans) =>
                handleModuleComplete('JK_SALES_RETAIL', {
                  answers: ans,
                  itemsPresented: items.map((i) => ({ id: i.id, correctAnswer: i.correctAnswer })),
                })
              }
              initialAnswers={
                ((answers.JK_SALES_RETAIL as { answers?: Record<string, TrueFalseNoSeAnswer> })?.answers) ?? {}
              }
            />
          );
        })()}

      {instrumentId === 'ML_8' && (
        <ML8Runner
          commercialName={commercialName}
          onComplete={(c) => handleModuleComplete('ML_8', c)}
          initialChoices={(answers.ML_8 as import('@/config/instruments').ML8OptionLetter[]) ?? []}
        />
      )}

      {instrumentId === 'APO_6' && (
        <APO6Runner
          commercialName={commercialName}
          onComplete={(c) => handleModuleComplete('APO_6', c)}
          initialChoices={(answers.APO_6 as import('@/config/instruments').APO6OptionLetter[]) ?? []}
        />
      )}

      {instrumentId === 'CULTURE_FIT' && (
        <CultureFitRunner
          onComplete={(results) => handleModuleComplete('CULTURE_FIT', results)}
        />
      )}

      {!['PCL_10', 'EEL_12', 'MINI_IPIP_20', 'RA_10', 'RFB_SJ_8', 'JK_OFFICE_ADMIN', 'JK_SALES_RETAIL', 'ML_8', 'APO_6', 'CULTURE_FIT'].includes(instrumentId) && (
        <div className="mx-auto max-w-2xl rounded-lg border bg-card p-6 text-center">
          <p className="text-muted-foreground">
            La prueba &quot;{commercialName}&quot; no está disponible en esta versión.
          </p>
        </div>
      )}
    </div>
  );
}
